<script setup lang="ts">
import { getProductRoute } from "@shopware/helpers";
import { onClickOutside, useFocus, useMagicKeys } from "@vueuse/core";

// True when the search bar is active and user can type in the search field
const active = ref(false);

// Reference to the search container
const searchContainer = ref(null);
const searchInput = ref();
const localePath = useLocalePath();
const { getLanguageIdFromCode, languages } =
  useInternationalization(localePath);
const { apiClient } = useShopwareContext();
const { locale } = useI18n();

const { status, data, refresh } = useAsyncData(
  'getSearchSuggest',
  async () => {
    const response = await apiClient.invoke(
      "searchSuggest post /search-suggest",
      {
        body: {
          search: typingQuery.value,
          limit: 6,
          associations: {
            seoUrls: {},
          },
        },
        headers: {
          "sw-language-id": getLanguageIdFromCode(locale.value),
        },
      }
    );

    return response.data;
  },
  { immediate: false, server: false }
);

watch(active, (value) => {
  const { focused } = useFocus(searchInput);

  focused.value = value;
});

// String the user has typed in the search field
const typingQuery = ref("");

watch(typingQuery, (value) => {
  if (value.length >= 3) {
    performSuggestSearch();
  }
});

// Defer the search request to prevent the search from being triggered too after typing
const performSuggestSearch = useDebounceFn(() => {
  refresh();
}, 500);

// Suggest results will only be shown, when the search bar is active and the user has typed more than three characters in the search field
const showSuggest = computed(() => {
  return typingQuery.value.length >= 3 && active.value;
});

if (import.meta.client) {
  onClickOutside(searchContainer, () => {
    active.value = false;
  });
}

const { enter } = useMagicKeys({ target: searchInput });

watch(enter, async (value) => {
  if (!value) return;

  active.value = false;
  await navigateTo({ path: "/search", query: { search: typingQuery.value } });
});
</script>

<template>
  <section ref="searchContainer" class="relative">
    <IconField class="hover:cursor-pointer">
      <InputIcon>
        <i class="pi pi-search text-surface-500" />
      </InputIcon>

      <InputText
        ref="searchInput"
        v-model="typingQuery"
        :placeholder="$t('form.searchPlaceholder')"
        class="w-full"
        @click="active = true"
      />
    </IconField>

    <div
      class="absolute bg-white dark:bg-gray-900 top-full left-0 z-20 w-full drop-shadow rounded-b-lg"
    >
      <div v-if="status === 'pending'" class="p-4 flex place-items-center">
        <ProgressSpinner style="width: 50px; height: 50px" stroke-width="5" />
      </div>

      <TransitionGroup
        v-else-if="showSuggest && data"
        tag="div"
        name="list"
        class="flex flex-wrap gap-4 p-2"
      >
        <NuxtLink
          v-for="product in data?.elements"
          :key="product.id"
          :to="getProductRoute(product)"
          class="w-full"
          @click="active = false"
        >
          <SearchBarItem
            :product="product"
            class="hover:bg-gray-100 transition-colors cursor-pointer rounded-lg"
          />
        </NuxtLink>

        <NuxtLink
          v-if="data?.total > 0"
          class="text-center w-full text-primary py-2 border-t"
          :to="{ path: '/search', query: { search: typingQuery } }"
          @click="active = false"
          >{{ $t("search.goToSearchPage") }}</NuxtLink
        >
      </TransitionGroup>

      <div
        v-if="status === 'success' && data?.total < 1"
        class="p-4 text-center"
      >
        {{ $t("listing.noProducts") }}
      </div>
    </div>
  </section>
</template>
